// Outils du mouvement : ce que les apparitions (apparitions.ts), les gestes (gestes.ts) et les
// renvois (renvois.ts) partagent. Déclencheurs, départs, calques, et le signal « posé ».

import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';

import { DUREE, ECHELON, ECHELON_MAX, MONTEE, SEUIL } from './reglages.ts';

gsap.registerPlugin(SplitText);

/** Un effet : il lit son élément et pose son mouvement, plein ou réduit. */
export type Effet = (el: HTMLElement, plein: boolean) => void;

// Le déclencheur d'une apparition, à étaler dans ses réglages. Le héros et la barre du haut sont à
// l'écran dès le chargement : ce qui s'y trouve entre tout de suite, sans attendre le défilement.
export const declencheur = (
  el: Element,
  start: string = SEUIL,
): { scrollTrigger?: ScrollTrigger.Vars } =>
  el.closest('.hero, .top') ? {} : { scrollTrigger: { trigger: el, start, once: true } };

export const delai = (el: HTMLElement) => Number(el.dataset.motionDelai ?? 0);

/**
 * Départ d'une apparition : monter et apparaître, ou seulement apparaître (mouvement réduit). À la
 * fin, aucun style ne reste en ligne.
 */
export const depart = (plein: boolean, montee: number | string = MONTEE): gsap.TweenVars => ({
  ...(plein ? { y: montee, opacity: 0 } : { opacity: 0 }),
  clearProps: 'transform,opacity',
});

/** Un court fondu, déclenché par el : toute apparition en mouvement réduit. */
export const fondu = (el: Element, cibles: gsap.TweenTarget = el) =>
  gsap.from(cibles, { ...depart(false), duration: DUREE.etat, ...declencheur(el) });

// Le texte d'un élément tel qu'il se lit : un élément en bloc (les deux lignes du nom) le sépare de
// ses voisins, ce que textContent ne fait pas.
function lecture(n: Node): string {
  if (!(n instanceof Element)) return n.textContent ?? '';
  return [...n.childNodes]
    .map((c) => {
      const t = lecture(c);
      return c instanceof Element && !getComputedStyle(c).display.startsWith('inline')
        ? ` ${t} `
        : t;
    })
    .join('');
}

/**
 * Découpe le texte d'un élément (SplitText), sans le taire aux lecteurs d'écran. Laissé à lui-même,
 * SplitText cache les morceaux et lit le texte par un aria-label, qu'un paragraphe ne peut pas
 * porter : il se tairait tant qu'il reste découpé. Ici, les morceaux sont cachés et une copie hors
 * écran (.lecteur) lit le texte à leur place ; défaire la découpe (revert) la retire avec le reste.
 */
export function decouper(el: HTMLElement, vars: SplitText.Vars): SplitText {
  const texte = lecture(el).replace(/\s+/g, ' ').trim();
  return SplitText.create(el, {
    ...vars,
    aria: 'none',
    onSplit: (s) => {
      for (const m of [...s.lines, ...s.words, ...s.chars]) m.setAttribute('aria-hidden', 'true');
      const copie = document.createElement('span');
      copie.className = 'lecteur';
      copie.textContent = texte;
      el.append(copie);
      vars.onSplit?.(s);
    },
  });
}

/** Décalage d'une liste de n éléments, plafonné. */
export const echelons = (n: number) => Math.min(ECHELON_MAX, ECHELON * Math.max(0, n - 1));

/**
 * Le compteur d'un élément : la valeur de son nombre, et de quoi l'écrire à une valeur donnée. Ce
 * qui le fait compter (apparition, geste) mène le temps. Sans nombre : undefined.
 */
export function compteur(
  el: HTMLElement,
): { cible: number; ecrire: (v: number) => void } | undefined {
  // Le nombre est le premier texte à chiffres de l'élément. Il se cherche à chaque écriture : un
  // surlignage qui l'englobe le découpe, puis se défait.
  const texte = () => {
    const m = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    while (m.nextNode()) {
      const n = m.currentNode;
      if (n instanceof Text && /\d/.test(n.data)) return n;
    }
    return undefined;
  };
  const original = texte()?.data ?? '';
  const trouve = /([−-]?)(\d+)/.exec(original);
  if (!trouve) return undefined;
  const [nombre, signe = '', chiffres = '0'] = trouve;
  const cible = Number(chiffres);
  // La largeur finale, en em, tenue tant que le nombre n'est pas à sa valeur : rien ne bouge
  // autour de lui. Dans le texte courant, il compte en bloc en ligne, sans couper. À sa valeur, il
  // ne garde aucun style en ligne.
  const style = getComputedStyle(el);
  const enLigne = style.display === 'inline';
  const taille = parseFloat(style.fontSize) || 16;
  const largeur = `${el.getBoundingClientRect().width / taille}em`;
  const ecrire = (v: number) => {
    const n = Math.round(v);
    const t = texte();
    if (t) t.data = original.replace(nombre, `${n ? signe : ''}${n}`);
    if (n !== cible) {
      if (enLigne) gsap.set(el, { display: 'inline-block', width: largeur, whiteSpace: 'nowrap' });
      else el.style.minWidth = largeur;
    } else if (enLigne) gsap.set(el, { clearProps: 'display,width,whiteSpace' });
    else el.style.removeProperty('min-width');
  };
  return { cible, ecrire };
}

// Montage : ce que les gestes ajoutent à la page le temps de jouer (volets, repères, traits) et
// les écouteurs qu'ils attachent. Le démontage (changement de mode, rechargement à chaud) retire
// tout ce qui reste.
const calques = new Set<Element>();
let arret = new AbortController();

/** Un calque décoratif, ajouté à parent le temps d'un geste. */
export function calque(balise: string, classe: string, parent: Element): HTMLElement {
  const e = document.createElement(balise);
  e.className = classe;
  e.setAttribute('aria-hidden', 'true');
  parent.append(e);
  calques.add(e);
  return e;
}

/** Retire un calque dont le geste est fini. */
export function retirer(e: Element): void {
  e.remove();
  calques.delete(e);
}

/** Le signal du montage en cours : un écouteur s'y attache et tombe avec lui. */
export const signal = () => arret.signal;

/** Retire les calques et les écouteurs des gestes ; le montage suivant repart à neuf. */
export function demonter(): void {
  arret.abort();
  arret = new AbortController();
  for (const e of calques) e.remove();
  calques.clear();
}

// Posé : un élément dont l'apparition est finie le signale. Un geste qui l'attend (le paquet sur
// la chaîne, un renvoi sur son déclencheur) part à ce moment, mesures prises dans la page au repos.
const POSE = 'mouvement:pose';

export function poser(el: Element): void {
  el.dispatchEvent(new Event(POSE));
}

/** Appelle f quand el est posé, une fois. */
export function quandPose(el: Element, f: () => void): void {
  el.addEventListener(POSE, f, { once: true, signal: signal() });
}
